import React, { useState, useRef, useMemo, useEffect } from "react";
import { Formik, Form } from "formik";
import Select from "react-select";
import TemplateDropdown from "../../../../utils/TemplateDropdown";
import {
  useCommunicationOperations,
  useBatchStatus,
  useCustomTemplates
} from "../../../../services/redux/api/components";
import { useLazyGetInsurersQuery } from "../../../../services/redux/api/baseApi";
import ListView from "../../shared/ListView";
import MessageEditor from "../../../../utils/MessageEditor";
import { toast } from "react-toastify";

const CommunicationToast = ({ channel = "email", status = "sending", title }) => {

  const isEmail = channel === "email";
  const isWhatsapp = channel === "whatsapp";

  return (
    <div className="flex flex-col w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* top bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: status === "success"
            ? 'linear-gradient(90deg, #16A34A, #15803D)'
            : 'linear-gradient(90deg, #DC2626, #B91C1C)',
          borderRadius: '4px 4px 0 0',
        }}
      />

      <div className="flex items-center gap-3 pt-2">

        {/* spinner */}
        {status === "sending" && (
          <div
            className="w-9 h-9 border-4 rounded-full animate-spin"
            style={{
              borderColor: isWhatsapp ? "#DCFCE7" : "#FECACA",
              borderTopColor: isWhatsapp ? "#16A34A" : "#DC2626",
              flexShrink: 0
            }}
          />
        )}

        {/* success tick */}
        {status === "success" && (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background: "#DCFCE7",
              color: "#16A34A",
              fontSize: 18,
              fontWeight: "bold"
            }}
          >
            ✔
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>
            {status === "sending"
              ? isEmail ? "Sending Emails..." : "Sending WhatsApp..."
              : isEmail ? "Emails Sent Successfully" : "WhatsApp Sent Successfully"}
          </p>

          <p style={{ fontSize: 11, color: '#6B7280', margin: '2px 0 0' }}>
            {title}
          </p>
        </div>
      </div>
    </div>
  );
};

const channelOptions = [
  { value: "whatsapp", label: "WhatsApp" },
  // { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
];

const channelSelectStyles = {
  control: (base, state) => ({
    ...base,
    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
    minHeight: "45px",
    borderRadius: "10px",
    border: state.isFocused ? "1px solid #EB2B21" : "1px solid #D1D5DB",
    fontSize: "14px",
    backgroundColor: "#fff",
    outline: "none",
    "&:hover": {
      borderColor: state.isFocused ? "#EB2B21" : "#D1D5DB",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: "14px",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "14px",
    color: "#212529",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "14px",
    color: "#9CA3AF",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "10px",
    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.15)",
  }),
};

const CommunicationMaster = ({
  hideTemplate = false,
  hideChannel = false,
  hidePreview = false,
  isModal = false,
  onSelectionChange,
  // ⭐ NEW PROPS
  defaultSelectedIds = [],
  defaultType = "customer",
  defaultFilters = [],
  defaultFilterMode = "with",
  defaultChannel = "",
  defaultTemplateId = null,
  defaultTemplateName = null,
  // ⭐ Policy-specific data from dashboard selection (policyNumber, dueDate, amount, productName per customerId)
  policyData = [],
  isDirectSelection = false,
}) => {
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [editableBody, setEditableBody] = useState("");
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [lastChannel, setLastChannel] = useState(defaultChannel || null);
  const [sortingOrder, setSortingOrder] = useState("memberwise");

  const [templateSource, setTemplateSource] = useState("meta");

  const { data: customTemplatesResponse } = useCustomTemplates({ pageSize: 1000 });
  const customTemplateOptions = useMemo(() => {
    const list = customTemplatesResponse?.data || [];
    return list.map(item => ({
      ...item,
      value: item.id,
      label: item.template_name,
    }));
  }, [customTemplatesResponse]);

  const handleTemplateSourceChange = (source, setFieldValue, currentChannel) => {
    setTemplateSource(source);
    setSelectedTemplateData(null);
    setEditableBody("");
    setFieldValue("template", "");
    if (source === "custom") {
      if (currentChannel === "whatsapp") {
        setFieldValue("channel", "email");
      }
    } else {
      if (currentChannel === "sms") {
        setFieldValue("channel", "email");
      }
    }
  };

  const filteredChannelOptions = useMemo(() => {
    if (templateSource === "custom") {
      return [
        { value: "sms", label: "SMS" },
        { value: "email", label: "Email" },
      ];
    }
    return [
      { value: "whatsapp", label: "WhatsApp" },
      { value: "email", label: "Email" },
    ];
  }, [templateSource]);
  const [selected, setSelected] = useState(true);
  const [customerFilter, setCustomerFilter] = useState({
    with: {
      life: false,
      health: false,
      motor: false,
      other: false,
    },
    without: {
      life: false,
      health: false,
      motor: false,
      other: false,
    },
  });
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [insurersByFilter, setInsurersByFilter] = useState([]);
  const [lifeInsurerIds, setLifeInsurerIds] = useState([]);
  const [healthInsurerIds, setHealthInsurerIds] = useState([]);
  const [motorInsurerIds, setMotorInsurerIds] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const {
    getFilteredCustomers,
    sendCommunication,
    sendPremiumCommunication,
    isProcessing: isCommProcessing
  } = useCommunicationOperations();
  const [triggerGetInsurers] = useLazyGetInsurersQuery();
  const gridRef = useRef(null);
  // Flag: set to true when user switches between Member Wise / Group Wise.
  // The first selection event after a mode switch must NOT carry over IDs
  // from the previous mode (e.g. CustomerIDs leaking into Group mode).
  const modeJustSwitchedRef = useRef(false);
  const [previewEditor, setPreviewEditor] = useState(null);


  // useEffect(() => {
  //   if (!isModal) return;
  //   if (!defaultFilters?.length) return;

  //   const newFilterState = {
  //     with: {
  //       life: false,
  //       health: false,
  //       motor: false,
  //       other: false
  //     },
  //     without: {
  //       life: false,
  //       health: false,
  //       motor: false,
  //       other: false
  //     }
  //   };

  //   defaultFilters.forEach(filter => {
  //     if (newFilterState.with.hasOwnProperty(filter)) {
  //       newFilterState.with[filter] = true;
  //     }
  //   });

  //   defaultFilters.forEach(filter => {
  //     if (newFilterState[defaultFilterMode]?.hasOwnProperty(filter)) {
  //       newFilterState[defaultFilterMode][filter] = true;  // ⭐ use mode
  //     }
  //   });

  //   setCustomerFilter(newFilterState);

  // }, [isModal, defaultFilters,defaultFilterMode]);

  useEffect(() => {
    if (!isModal) return;
    if (!defaultFilters?.length) return;

    const newFilterState = {
      with: {
        life: false,
        health: false,
        motor: false,
        other: false
      },
      without: {
        life: false,
        health: false,
        motor: false,
        other: false
      }
    };

    // ✅ Only set filters in the correct mode section
    const mode = defaultFilterMode === "without" ? "without" : "with";

    defaultFilters.forEach(filter => {
      if (newFilterState[mode].hasOwnProperty(filter)) {
        newFilterState[mode][filter] = true;
      }
    });

    setCustomerFilter(newFilterState);

  }, [isModal, defaultFilters, defaultFilterMode]);



  const defaultSelectedIdsStr = defaultSelectedIds?.join(",");

  useEffect(() => {
    if (!isModal) return;
    if (defaultType === "group") {
      setSortingOrder("groupwise");
    } else {
      setSortingOrder("memberwise");
    }

    // 🔥 RESTORE: Initialize state from props so payload isn't empty on first toggle
    if (defaultSelectedIds?.length) {
      const numericIds = defaultSelectedIds.map(id => Number(id)).filter(id => !isNaN(id));
      setSelectedCustomerIds(numericIds);
    }
  }, [defaultType, defaultSelectedIdsStr, isModal]);

  useEffect(() => {
    if (!isModal) return;

    const anyWithSelected = Object.values(customerFilter.with).some(v => v);
    const anyWithoutSelected = Object.values(customerFilter.without).some(v => v);

    if (anyWithSelected || anyWithoutSelected) {
      handleApplyFilter();
    }

  }, [customerFilter]);

  useEffect(() => {
    if (!isModal) return;
    if (!defaultSelectedIds?.length) return;

    // Auto-apply filter so customers load (even with no filter checkboxes)
    const anyFilterAlreadySet =
      Object.values(customerFilter.with).some(v => v) ||
      Object.values(customerFilter.without).some(v => v);

    if (!anyFilterAlreadySet) {
      // Force fetch all customers so pre-selected ones appear
      // 🔥 FIX: Pass numericIds directly to avoid state lag on first load
      const initialSort = defaultType === "group" ? "groupwise" : "memberwise";
      const numericIds = defaultSelectedIds.map(id => Number(id)).filter(id => !isNaN(id));
      fetchFilteredCustomers(
        customerFilter,
        lifeInsurerIds,
        healthInsurerIds,
        motorInsurerIds,
        1,
        pageSize,
        numericIds,
        initialSort
      );
    }
  }, [isModal, defaultSelectedIdsStr, defaultType]);



  const {
    data: batchStatus,
    isFetching: isBatchChecking
  } = useBatchStatus(currentBatchId, {
    pollingInterval: currentBatchId ? 4000 : 0// check every 4 sec
  });

  // useEffect(() => {
  //   if (!batchStatus || !currentBatchId) return;
  //   const total = Number(batchStatus.total || 0);
  //   const success = Number(batchStatus.success || 0);
  //   const failed = Number(batchStatus.failed || 0);
  //   const pending = Number(batchStatus.pending || 0);

  //   // still processing
  //   if (success + failed < total) {
  //     return; // keep polling
  //   }

  //   if (batchStatus.pending === 0) {

  //     toast.dismiss("commToast");

  //     if (batchStatus.failed === 0) {

  //       toast(
  //         <CommunicationToast
  //           channel={lastChannel}
  //           status="success"
  //         />,
  //         { autoClose: 4000 }
  //       );

  //     } else {
  //       toast.error(
  //         `${batchStatus.success} sent, ${batchStatus.failed} failed`
  //       );
  //     }

  //     setCurrentBatchId(null);
  //   }

  // }, [batchStatus, currentBatchId]);


  //   useEffect(() => {
  //   if (!batchStatus || !currentBatchId) return;

  //   const total = Number(batchStatus.total || 0);
  //   const success = Number(batchStatus.success || 0);
  //   const failed = Number(batchStatus.failed || 0);
  //   const pending = Number(batchStatus.pending || 0);

  //   // If worker hasn't finished yet
  //   if (pending > 0) {
  //     toast.dismiss("commToast");

  //     toast.success("Email scheduled successfully", {
  //       toastId: "scheduledToast",
  //       autoClose: 3000
  //     });

  //     return;
  //   }

  //   // Worker finished processing
  //   if (success + failed >= total) {

  //     toast.dismiss("commToast");

  //     if (failed === 0) {
  //       toast(
  //         <CommunicationToast
  //           channel={lastChannel}
  //           status="success"
  //         />,
  //         { autoClose: 4000 }
  //       );
  //     } else {
  //       toast.error(`${success} sent, ${failed} failed`);
  //     }

  //     setCurrentBatchId(null);
  //   }

  // }, [batchStatus, currentBatchId]);

  const handleTemplateSelect = (value, selectedOption) => {
    if (!selectedOption) {
      setSelectedTemplateData(null);
      setEditableBody("");
      return;
    }
    setSelectedTemplateData(selectedOption);
    setEditableBody("");
  };

  const buildPayload = (
    filters,
    lifeIds,
    healthIds,
    motorIds,
    page = pageNumber,
    size = pageSize,
    sortOption = sortingOrder,
    overrideIds = null
  ) => {
    const selectedWith = Object.keys(filters.with).filter(
      (key) => filters.with[key]
    );

    const selectedWithout = Object.keys(filters.without).filter(
      (key) => filters.without[key]
    );

    let mode = "";
    let selectedFilters = [];

    if (selectedWith.length > 0) {
      mode = "with";
      selectedFilters = selectedWith;
    }

    if (selectedWithout.length > 0) {
      mode = "without";
      selectedFilters = selectedWithout;
    }
    const isGroupMode = sortOption === "groupwise";
    return {
      pageNumber: page,
      pageSize: size,
      mode,
      filters: selectedFilters,
      lifeinsurance: lifeIds || [],
      healthinsurance: healthIds || [],
      motorinsurance: motorIds || [],
      sortingOrder: sortOption,
      customerIds:
        selected && !isGroupMode && (overrideIds || selectedCustomerIds).length > 0
          ? (overrideIds || selectedCustomerIds)
          : null,

      groupIds:
        selected && isGroupMode && (overrideIds || selectedCustomerIds).length > 0
          ? (overrideIds || selectedCustomerIds)
          : null,

      excludeCustomerIds:
        !selected && !isGroupMode && selectedCustomerIds.length > 0
          ? selectedCustomerIds
          : null,

      excludeGroupIds:
        !selected && isGroupMode && selectedCustomerIds.length > 0
          ? selectedCustomerIds
          : null
    };
  };


  const fetchFilteredCustomers = async (filter, lifeIds, healthIds, motorIds, page, size, overrideIds = null, sortOption = sortingOrder) => {
    try {
      setIsFilterLoading(true);
      const payload = buildPayload(
        filter,
        lifeIds,
        healthIds,
        motorIds,
        page,
        size,
        sortOption,
        overrideIds
      );
      const response = await getFilteredCustomers(payload).unwrap();
      const rows = Array.isArray(response?.data) ? response.data : [];
      const total = response?.total || rows.length;
      setTotalCustomers(total);

      const sanitize = (val) => (typeof val === "string" ? val.replace(/\0/g, "").trim() : val);

      const mapped = rows.map((row, index) => ({
        id: row.CustomerId ?? row.CustomerID ?? row.id ?? index,
        groupId: row.GroupID ?? row.groupId ?? null,
        groupCode: row.GroupCode ?? "",
        fullName: sanitize(row.FullName ?? ""),
        groupHeadName: sanitize(row.GroupHeadName ?? ""),
        mobile: sanitize(row.Mobile ?? row.MobileNo ?? ""),
        email: sanitize(row.Email ?? "")
      }));

      // 🔥 Move selected items to top – convert defaultSelectedIds to numbers
      if (defaultSelectedIds?.length) {
        const numericSelectedIds = defaultSelectedIds.map(id => Number(id));
        mapped.sort((a, b) => {
          const aSelected = defaultType === "group"
            ? numericSelectedIds.includes(a.groupId)
            : numericSelectedIds.includes(a.id);
          const bSelected = defaultType === "group"
            ? numericSelectedIds.includes(b.groupId)
            : numericSelectedIds.includes(b.id);
          return bSelected - aSelected;
        });
      }

      // Add fallback mapping if API returned no matching rows
      if (isDirectSelection && mapped.length === 0 && defaultSelectedIds?.length > 0) {
        defaultSelectedIds.forEach((id) => {
          const matchedPolicy = policyData.find(pd => Number(pd.customerId) === Number(id));
          mapped.push({
            id: Number(id),
            groupId: null,
            groupCode: matchedPolicy?.groupCode || "",
            fullName: matchedPolicy?.customerName || `Customer ${id}`,
            groupHeadName: "",
            mobile: matchedPolicy?.mobile || "",
            email: matchedPolicy?.email || "",
          });
        });
        setTotalCustomers(mapped.length);
      }

      setFilteredCustomers(mapped);
      setHasAppliedFilter(true);
    } catch (err) {
      console.error("Error fetching filtered customers:", err);
      let mapped = [];
      if (isDirectSelection && defaultSelectedIds?.length > 0) {
        defaultSelectedIds.forEach((id) => {
          const matchedPolicy = policyData.find(pd => Number(pd.customerId) === Number(id));
          mapped.push({
            id: Number(id),
            groupId: null,
            groupCode: matchedPolicy?.groupCode || "",
            fullName: matchedPolicy?.customerName || `Customer ${id}`,
            groupHeadName: "",
            mobile: matchedPolicy?.mobile || "",
            email: matchedPolicy?.email || "",
          });
        });
        setTotalCustomers(mapped.length);
      }
      setFilteredCustomers(mapped);
      setHasAppliedFilter(true);
    }
  };



  useEffect(() => {
    if (!hasAppliedFilter) return;

    setPageNumber(1);

    fetchFilteredCustomers(
      customerFilter,
      lifeInsurerIds,
      healthInsurerIds,
      motorInsurerIds,
      1,
      pageSize
    );

  }, [selected, sortingOrder]);

  const toggleFilter = (section, key) => {
    setCustomerFilter((prev) => {
      const currentSection = prev[section];
      const otherSectionName = section === "with" ? "without" : "with";
      const otherSection = prev[otherSectionName];

      const toggledOn = !currentSection[key];

      const updatedCurrentSection = {
        ...currentSection,
        [key]: toggledOn,
      };

      const hasAnySelected = Object.values(updatedCurrentSection).some(
        (value) => value
      );

      let updatedOtherSection = otherSection;

      if (hasAnySelected) {
        updatedOtherSection = Object.keys(otherSection).reduce((acc, k) => {
          acc[k] = false;
          return acc;
        }, {});
      }

      const updatedFilters = {
        with:
          section === "with"
            ? updatedCurrentSection
            : updatedOtherSection,
        without:
          section === "without"
            ? updatedCurrentSection
            : updatedOtherSection,
      };

      const anyFilterSelected =
        Object.values(updatedFilters.with).some((v) => v) ||
        Object.values(updatedFilters.without).some((v) => v);

      if (!anyFilterSelected) {
        setFilteredCustomers([]);
        setHasAppliedFilter(false);
      }

      return updatedFilters;
    });
  };


  const handlePageChange = (page) => {
    setPageNumber(page);
    fetchFilteredCustomers(
      customerFilter,
      lifeInsurerIds,
      healthInsurerIds,
      motorInsurerIds,
      page,
      pageSize
    );
  };


  const handleItemsPerPageChange = (e) => {
    const newSize = Number(e.target.value) || 10;
    setPageSize(newSize);
    setPageNumber(1);
    fetchFilteredCustomers(
      customerFilter,
      lifeInsurerIds,
      healthInsurerIds,
      motorInsurerIds,
      1,
      newSize
    );
  };

  // const selectedWithFilters = Object.keys(customerFilter.with)
  // .filter(key => customerFilter.with[key]);

  // AFTER
  const anyWithSelected = Object.values(customerFilter.with).some(v => v);
  const selectedWithFilters = anyWithSelected
    ? Object.keys(customerFilter.with).filter(key => customerFilter.with[key])
    : Object.keys(customerFilter.without).filter(key => customerFilter.without[key]);

  const handleSortingOrderChange = (newOrder) => {
    setSortingOrder(newOrder);

    // Mark that mode just switched — next selection event must discard all
    // previously accumulated IDs so no cross-mode leakage occurs
    modeJustSwitchedRef.current = true;

    // Also visually deselect all grid rows immediately
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }

    if (!isModal) return;

    const newType = newOrder === "groupwise" ? "group" : "customer";
    if (newType === defaultType) {
      // Restore default selection — restore useEffect will re-tick matching rows
      const numericIds = (defaultSelectedIds || []).map(id => Number(id)).filter(id => !isNaN(id));
      setSelectedCustomerIds(numericIds);
      if (onSelectionChange) {
        onSelectionChange(
          numericIds,
          newType,
          selectedWithFilters,
          customerFilter.with ? (Object.values(customerFilter.with).some(v => v) ? "with" : "without") : "with"
        );
      }
    } else {
      // Clear selection — different type, start fresh
      setSelectedCustomerIds([]);
      if (onSelectionChange) {
        onSelectionChange(
          [],
          newType,
          selectedWithFilters,
          customerFilter.with ? (Object.values(customerFilter.with).some(v => v) ? "with" : "without") : "with"
        );
      }
    }
  };


  const handleCustomerSelectionChanged = (event) => {
    if (!gridRef.current?.api) return;
    if (event?.source === "api") return;

    try {
      const isGroupMode = sortingOrder === "groupwise";

      // 1. Get IDs from ALREADY SELECTED nodes in the CURRENT grid view
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const visibleSelectedIds = selectedNodes.map((node) =>
        Number(isGroupMode ? node.data.groupId : node.data.id)
      ).filter(id => !isNaN(id));

      // 2. Get IDs of ALL nodes currently in the CURRENT grid view
      const allVisibleIds = [];
      gridRef.current.api.forEachNode((node) => {
        const id = Number(isGroupMode ? node.data.groupId : node.data.id);
        if (!isNaN(id)) allVisibleIds.push(id);
      });

      setSelectedCustomerIds((prev) => {
        // If the mode JUST switched, ignore all previous IDs to prevent
        // CustomerIDs leaking into Group mode (or vice versa).
        // Reset the flag after consuming it.
        let preservedIds = [];
        if (modeJustSwitchedRef.current) {
          modeJustSwitchedRef.current = false; // consume the flag
          // preservedIds stays [] — fresh start for this mode
        } else {
          // 3. Keep previously selected items that are NOT in the current grid view
          preservedIds = prev
            .map(id => Number(id))
            .filter(id => !allVisibleIds.includes(id));
        }

        // 4. Combine with the new selections from the current grid view
        const finalIds = [...new Set([...preservedIds, ...visibleSelectedIds])];

        // Notify modal of new accumulated state
        if (onSelectionChange) {
          const anyWithSelected = Object.values(customerFilter.with).some(v => v);
          const filterMode = anyWithSelected ? "with" : "without";
          onSelectionChange(
            finalIds,
            isGroupMode ? "group" : "customer",
            selectedWithFilters,
            filterMode
          );
        }

        return finalIds;
      });
    } catch (error) {
      console.error("Error in selection tracking:", error);
    }
  };

  const handleApplyFilter = async () => {
    const filters = customerFilter;

    const anyWithSelected = Object.values(filters.with).some((v) => v);
    const anyWithoutSelected = Object.values(filters.without).some((v) => v);

    if (!anyWithSelected && !anyWithoutSelected) {
      setFilteredCustomers([]);
      setHasAppliedFilter(false);
      return;
    }

    const anyLifeSelected = filters.with.life || filters.without.life;
    const anyHealthSelected = filters.with.health || filters.without.health;
    const anyMotorSelected = filters.with.motor || filters.without.motor;

    let lifeIds = [];
    let healthIds = [];
    let motorIds = [];

    try {
      setIsFilterLoading(true);
      if (anyLifeSelected) {
        const respL = await triggerGetInsurers({ Insurer_Type: "L" }).unwrap();
        const listL = respL?.data || [];
        const idsL = listL
          .map((item) => item.Insurer_ID ?? item.insurerId ?? item.id)
          .filter((id) => id != null);
        lifeIds = idsL;
        setLifeInsurerIds(idsL);
      }

      if (anyHealthSelected || anyMotorSelected) {
        const respG = await triggerGetInsurers({ Insurer_Type: "G" }).unwrap();
        const listG = respG?.data || [];
        const idsG = listG
          .map((item) => item.Insurer_ID ?? item.insurerId ?? item.id)
          .filter((id) => id != null);

        if (anyHealthSelected) {
          healthIds = idsG;
          setHealthInsurerIds(idsG);
        }

        if (anyMotorSelected) {
          motorIds = idsG;
          setMotorInsurerIds(idsG);
        }
      }

      setPageNumber(1);
      await fetchFilteredCustomers(
        filters,
        lifeIds,
        healthIds,
        motorIds,
        1,
        pageSize
      );
    } catch (error) {
      console.error("Error applying filter:", error);
      setFilteredCustomers([]);
      setHasAppliedFilter(false);
    } finally {
      setIsFilterLoading(false);
    }
  };


  const buildMessageFromTemplate = (channel) => {
    if (!selectedTemplateData) return { subject: "", body: "" };

    let subject = "";
    let raw = "";

    if (channel === "email") {
      subject = selectedTemplateData.email_subject || "";
      raw = selectedTemplateData.email_html || "";
      raw = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    } else if (channel === "sms") {
      raw = selectedTemplateData.sms_text || "";
    } else if (channel === "whatsapp") {
      raw = selectedTemplateData.whatsapp_text || "";
    }

    return { subject, body: raw };
  };

  const handleSend = async (channel, subject, body) => {
    if (!channel || !selectedTemplateData || !body) return;

    if (!selectedCustomerIds.length) {
      toast.error("Select customer to send message");
      return;
    }

    const payload = {
      channel,
      templateId: selectedTemplateData.value ?? selectedTemplateData.id ?? null,
      customBody: body,
      customSubject: subject,
      customerIds: selectedCustomerIds,
      targetType: sortingOrder === "groupwise" ? "GROUP" : "CUSTOMER",
      // Pass the specific policy details per customer so backend uses correct policy
      policyData: policyData.filter(pd => selectedCustomerIds.includes(Number(pd.customerId))),
      isCustom: templateSource === "custom",
    };

    try {
      const commCode = String(selectedTemplateData.communication_code || '').toUpperCase();
      const tempIdStr = String(selectedTemplateData.value || '');
      const isPremiumDue = ['1', '54', '55'].includes(tempIdStr) || commCode === 'PREMIUM_DUE';
      const isPremiumOutstanding = ['6', '56', '57'].includes(tempIdStr) || commCode === 'PREMIUM_OUTSTANDING';

      let res;
      if (isPremiumDue || isPremiumOutstanding) {
        res = await sendPremiumCommunication(payload).unwrap();
      } else {
        res = await sendCommunication(payload).unwrap();
      }

      setLastChannel(channel);
      setCurrentBatchId(res.batchId);

      toast.dismiss("commToast");

      setLastChannel(channel);

      toast.dismiss("commToast");

      toast(
        <CommunicationToast channel={channel} status="sending" />,
        {
          toastId: "commToast",
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        }
      );

      // simulate queue scheduling
      setTimeout(() => {

        toast.dismiss("commToast");

        toast.success("Email scheduled successfully", {
          autoClose: 3000
        });

      }, 2000);

      // setCurrentBatchId(res.batchId); // 👈 start tracking batch
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.msg ||
        err?.error ||
        "Failed to send communication";
      toast.error(msg);
    } finally {
      setIsPreviewOpen(false);
    }
  };

  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      suppressMenu: true,
      pinned: "left"
    },
    { headerName: "Group Code", field: "groupCode", headerClass: "text-right-header", cellClass: "text-right" },
    {
      headerName: sortingOrder === "groupwise" ? "Group Head Name" : "Customer Name",
      field: sortingOrder === "groupwise" ? "groupHeadName" : "fullName", headerClass: "text-left-header", cellClass: "text-left"

    },
    { headerName: "Mobile", field: "mobile", headerClass: "text-right-header", cellClass: "text-right" },
    { headerName: "Email", field: "email", headerClass: "text-left-header", cellClass: "text-left" },
  ], [sortingOrder]);



  useEffect(() => {
    if (!isModal) return;

    // 🔥 RESTORE: Use current state if available, fallback to props only on initial load for default mode
    const isCurrentModeDefault = (sortingOrder === "groupwise" && defaultType === "group") ||
      (sortingOrder === "memberwise" && defaultType === "customer");
    const currentSelection = (selectedCustomerIds && selectedCustomerIds.length > 0)
      ? selectedCustomerIds
      : (isCurrentModeDefault ? defaultSelectedIds : []);

    if (!currentSelection?.length) return;
    if (!filteredCustomers?.length) return;
    if (!gridRef.current?.api) return;

    const numericIds = currentSelection.map(id => Number(id));

    setTimeout(() => {
      if (!gridRef.current?.api) return;
      gridRef.current.api.forEachNode((node) => {
        const id = node.data?.id;
        const groupId = node.data?.groupId;

        const isMatch = sortingOrder === "groupwise"
          ? numericIds.includes(Number(groupId))
          : numericIds.includes(Number(id));

        if (isMatch) {
          node.setSelected(true);
        }
      });
    }, 300);
  }, [filteredCustomers, isModal, sortingOrder]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        customer: null,
        channel: defaultChannel || "",
        template: defaultTemplateId || "",
      }}
      onSubmit={() => { }}
    >
      {({ values, handleChange, setFieldValue }) => {
        const hasSelection = !!(values.channel && selectedTemplateData);
        const preview = hasSelection
          ? buildMessageFromTemplate(values.channel)
          : { subject: "", body: "" };
        const previewBody = preview.body;
        const previewSubject = preview.subject;

        const placeholderTokens = [
          "{{customer_name}}",
          "{{policy_number}}",
          "{{product_name}}",
          "{{policy_details_block}}",
        ];

        const insertTokenAtCursor = (token) => {
          if (previewEditor) {
            previewEditor.chain().focus().insertContent(token).run();
            return;
          }

          const current = editableBody || previewBody || "";
          const next = current + token;
          setEditableBody(next);
        };

        const selectedChannelOption =
          filteredChannelOptions.find((opt) => opt.value === values.channel) || null;

        const previewButtonLabel = "Preview & Send";

        const anyWithSelected = Object.values(customerFilter.with).some(
          (v) => v
        );
        const anyWithoutSelected = Object.values(
          customerFilter.without
        ).some((v) => v);

        return (
          <Form>
            <div className={isModal ? "w-full" : "w-full min-h-[calc(100vh-100px)] px-4 md:px-6 py-6 bg-slate-50"}>
              <div className={isModal ? "w-full" : "w-full mx-auto rounded-2xl bg-white border border-gray-100 shadow-lg"}>

                {/* Header */}
                {!isModal && (
                  <div className="p-2 border-b border-brandRed">
                    <h5 className="text-brandRed font-bold m-3">
                      Communication Master
                    </h5>
                  </div>
                )}

                {/* Template and Channel */}
                {!hideTemplate && !hideChannel && (
                  <div className="px-4 md:px-8 pt-8 pb-4">
                    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-6 md:gap-10 relative">
                      <div className="flex flex-col md:flex-row md:items-center gap-4 w-full md:w-auto">
                        <div className="flex flex-col gap-1">
                          <label className="font-semibold text-gray-600">
                            Template Source
                          </label>
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                              <input
                                type="radio"
                                name="templateSource"
                                value="meta"
                                checked={templateSource === "meta"}
                                onChange={() => handleTemplateSourceChange("meta", setFieldValue, values.channel)}
                                className="w-3.5 h-3.5 text-brandRed focus:ring-brandRed"
                              />
                              <span>Meta Verified</span>
                            </label>
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                              <input
                                type="radio"
                                name="templateSource"
                                value="custom"
                                checked={templateSource === "custom"}
                                onChange={() => handleTemplateSourceChange("custom", setFieldValue, values.channel)}
                                className="w-3.5 h-3.5 text-brandRed focus:ring-brandRed"
                              />
                              <span>Custom</span>
                            </label>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1 w-full md:w-[250px]">
                          <label className="font-semibold text-gray-600">
                            Template
                          </label>
                          {templateSource === "meta" ? (
                            <div className="[&_.form-label]:hidden">
                              <TemplateDropdown
                                name="template"
                                defaultTemplateName={defaultTemplateName}
                                onChange={(value, option) =>
                                  handleTemplateSelect(value, option)
                                }
                              />
                            </div>
                          ) : (
                            <Select
                              name="template"
                              options={customTemplateOptions}
                              value={customTemplateOptions.find(opt => opt.value === values.template) || null}
                              onChange={(option) => {
                                setFieldValue("template", option ? option.value : "");
                                handleTemplateSelect(option ? option.value : "", option);
                              }}
                              isClearable
                              classNamePrefix="react-select"
                              styles={channelSelectStyles}
                              placeholder="Select Custom Template"
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 md:w-auto">
                        <label className="font-semibold text-gray-600 w-full md:w-48">
                          Communication Channel
                        </label>
                        <div className="w-full md:w-[300px]">
                          <Select
                            name="channel"
                            options={filteredChannelOptions}
                            value={selectedChannelOption}
                            onChange={(option) =>
                              setFieldValue("channel", option ? option.value : "")
                            }
                            isClearable
                            classNamePrefix="react-select"
                            styles={channelSelectStyles}
                            placeholder="Select Channel"
                          />
                        </div>
                      </div>

                      <div className="flex justify-start md:justify-end w-full md:w-auto">
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedTemplateData || !values.channel) {
                              toast.error(
                                "Select template and communication channel to see the preview"
                              );
                              return;
                            }
                            setEditableBody(previewBody);
                            setIsPreviewOpen(true);
                          }}
                          className="px-7 py-2.5 w-[160px] rounded-md bg-brandRed text-white text-sm font-medium hover:bg-red-600 shadow  md:mt-0"
                        >
                          {previewButtonLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Select Customers */}
                <div className="px-4 md:px-8 pb-8">
                  {!isDirectSelection && (
                    <div className="mt-4 space-y-6">
                      <>
                        <div className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 rounded-md">
                          Select Customer With
                        </div>
                        <div className="px-4 py-1 flex flex-wrap items-center gap-4">
                          <div className="flex flex-wrap gap-4">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.with.life}
                                onChange={() => toggleFilter("with", "life")}
                              />
                              <span>Life Insurance</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.with.health}
                                onChange={() => toggleFilter("with", "health")}
                              />
                              <span>Health</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.with.motor}
                                onChange={() => toggleFilter("with", "motor")}
                              />
                              <span>Motor</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.with.other}
                                onChange={() => toggleFilter("with", "other")}
                              />
                              <span>Other</span>
                            </label>
                          </div>
                        </div>

                        <div className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 rounded-md">
                          Select Customer Without
                        </div>
                        <div className="px-4 py-1 flex flex-wrap items-center gap-4">
                          <div className="flex flex-wrap gap-4">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.without.life}
                                onChange={() => toggleFilter("without", "life")}
                              />
                              <span>Life Insurance</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.without.health}
                                onChange={() => toggleFilter("without", "health")}
                              />
                              <span>Health</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.without.motor}
                                onChange={() => toggleFilter("without", "motor")}
                              />
                              <span>Motor</span>
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={customerFilter.without.other}
                                onChange={() => toggleFilter("without", "other")}
                              />
                              <span>Other</span>
                            </label>
                          </div>
                        </div>

                        <div className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 rounded-md mt-6">
                          Sorting Order
                        </div>
                        <div className="px-4 py-3 flex flex-wrap items-center justify-start gap-4">

                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="sortingOrder"
                              value="memberwise"
                              className="w-4 h-4 text-brandRed focus:ring-brandRed border-gray-300"
                              checked={sortingOrder === "memberwise"}
                              onChange={(e) => handleSortingOrderChange(e.target.value)}
                            />
                            <span className="text-sm font-medium text-gray-700">Member Wise</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer ml-3 mr-3">
                            <input
                              type="radio"
                              name="sortingOrder"
                              value="groupwise"
                              className="w-4 h-4 text-brandRed focus:ring-brandRed border-gray-300"
                              checked={sortingOrder === "groupwise"}
                              onChange={(e) => handleSortingOrderChange(e.target.value)}
                            />
                            <span className="text-sm font-medium text-gray-700">Group Wise</span>
                          </label>


                          {isModal && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className={`toggle-btn ${selected === true ? "active" : ""}`}
                                onClick={() => setSelected(true)}
                              >
                                Selected
                              </button>
                              <button
                                type="button"
                                className={`toggle-btn ${selected === false ? "active" : ""}`}
                                onClick={() => setSelected(false)}
                              >
                                Not Selected
                              </button>
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={handleApplyFilter}
                            disabled={isFilterLoading}
                            className="px-6 py-2 min-w-[120px] rounded-md bg-brandRed text-white text-sm font-medium hover:bg-red-600 shadow disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {isFilterLoading && (
                              <span className="inline-block w-5 h-5 border-[3px] border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            <span>Filter</span>
                          </button>
                        </div>
                      </>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <span className="text-sm text-gray-500">
                      {isCommProcessing ? "Loading customers..." : ""}
                    </span>
                  </div>

                  {(hasAppliedFilter || isDirectSelection) && (
                    <div className="mt-6">
                      <ListView
                        showFilters={false}
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        rowData={filteredCustomers}
                        defaultColDef={{
                          sortable: true,
                          resizable: true,
                          flex: 1,
                          suppressMenu: true,
                        }}
                        loading={isCommProcessing}
                        totalCount={totalCustomers}
                        itemsPerPage={pageSize}
                        currentPage={pageNumber}
                        setCurrentPage={handlePageChange}
                        handleItemsPerPageChange={handleItemsPerPageChange}
                        onSelectionChanged={handleCustomerSelectionChanged}
                        suppressDeselectOnDataChange={true}
                      />
                    </div>
                  )}



                </div>

                {!hidePreview && isPreviewOpen && hasSelection && (
                  <div className="fixed inset-0 z-40 flex items-center justify-center bg-white bg-opacity-10 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl mx-4">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">
                          Message Preview
                        </h3>
                      </div>
                      <div className="px-4 py-3 flex gap-4">
                        <div className={`w-48 border border-gray-200 rounded-md bg-gray-50 p-3 ${selectedTemplateData?.template_type === "META" ? "opacity-50 pointer-events-none" : ""}`}>
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Data Include
                          </div>
                          <div className="flex flex-col gap-2">
                            {placeholderTokens.map((token) => (
                              <button
                                key={token}
                                type="button"
                                disabled={selectedTemplateData?.template_type === "META"}
                                className="px-2 py-1 rounded border border-gray-300 bg-white text-xs text-gray-700 text-left hover:bg-gray-100 flex items-center justify-between gap-2"
                              >
                                <span>{token}</span>
                                <span
                                  onClick={(e) => {
                                    if (selectedTemplateData?.template_type === "META") return;
                                    e.stopPropagation();
                                    insertTokenAtCursor(token);
                                  }}
                                  className="flex items-center justify-center w-4 h-4 rounded-full bg-brandRed text-white text-[10px] cursor-pointer"
                                >
                                  +
                                </span>
                              </button>
                            ))}
                          </div>
                          <div className="mt-3 text-[11px] text-gray-500">
                            Note: Add data include tokens by clicking on the tokens.
                          </div>
                        </div>
                        <div className="flex-1">
                          {values.channel === "sms" ? (
                            <textarea
                              rows={8}
                              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brandRed focus:border-brandRed font-sans text-sm resize-none"
                              value={editableBody || previewBody}
                              onChange={(e) => setEditableBody(e.target.value)}
                            />
                          ) : (
                            <MessageEditor
                              value={editableBody || previewBody}
                              onChange={(html) => setEditableBody(html)}
                              onReady={(editor) => setPreviewEditor(editor)}
                              editable={selectedTemplateData?.template_type !== "META"}
                            />
                          )}
                        </div>
                      </div>
                      <div className="px-4 py-3 flex items-center justify-center gap-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            handleSend(
                              values.channel,
                              previewSubject,
                              editableBody || previewBody
                            );
                          }}
                          disabled={isCommProcessing}
                          className="px-5 py-2 rounded-md bg-brandRed text-white text-sm font-medium hover:bg-red-600 shadow disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isCommProcessing
                            ? "Sending..."
                            : values.channel === "email"
                              ? "Send Email"
                              : values.channel === "sms"
                                ? "Send SMS"
                                : "Send WhatsApp"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsPreviewOpen(false)}
                          className="px-5 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CommunicationMaster